import type { SelectOption } from 'naive-ui'
import { defu } from 'defu'
import { get } from 'jsonpointer'
import { z } from 'zod'

type UnknownObject = {
  [key: string]: any
}

type Render = (option: SelectOption, idx: number) => string | { iconName: string } | undefined

type Options = {
  labelPointer?: string
  valuePointer?: string
  formatter?: (option: SelectOption, index: number) => SelectOption
  renderIcon?: (option: SelectOption, idx: number) => string | undefined
  renderPrefix?: Render
  renderSuffix?: Render
}

const defaultOptions = {
  labelPointer: '/name',
  valuePointer: '/id',
  formatter: (option: SelectOption) => option,
}

export default function (payload?: UnknownObject[], options?: Options): SelectOption[] {
  if (!payload) {
    return []
  }

  const renderIconOrString = (select: SelectOption, idx: number, render?: Render) => {
    if (!render) {
      return undefined
    }

    const result = render(select, idx)
    if (typeof result === 'object') {
      return useRenderIcon({ name: result.iconName, class: 'text-lg shrink-0' })
    }
    return h('span', undefined, { default: () => result })
  }

  const optionsWithDefault = defu(options, defaultOptions)

  return payload.map((data, idx) => {
    const labelValue = z.string().parse(get(data, optionsWithDefault.labelPointer))
    const value = z.string().parse(get(data, optionsWithDefault.valuePointer))
    const selectOption = optionsWithDefault.formatter({ label: labelValue, value }, idx)

    return {
      ...selectOption,
      label() {
        return h('div', { class: 'flex items-center gap-1' }, [
          renderIconOrString(selectOption, idx, options?.renderPrefix),
          selectOption.label as string,
          renderIconOrString(selectOption, idx, options?.renderSuffix),
        ])
      },
    }
  })
}
